/**
 * PdfPreviewPanel — Live-PDF neben dem Markdown-Editor (#2246).
 *
 * Zweite Sicht des Zwei-Sichten-Editors: Links der Markdown-Quellmodus,
 * rechts die Seiten so, wie der Export sie liefert. Gerendert wird auf dem
 * Server über denselben Weg wie der Export (content-api → markdown-api),
 * mit dem Text, der gerade im Editor steht. Eine Druckansicht im Browser
 * würde Umbrüche vortäuschen, die der Export anders setzt.
 *
 * Bei Tipp-Pause wird neu gerendert; ein laufender Render wird abgebrochen,
 * wenn neuer Text kommt. Die alten Seiten bleiben stehen, bis die neuen da
 * sind, und die Scroll-Position bleibt erhalten.
 */

import { useEffect, useRef, useState } from 'react'
// `?worker` lässt Vite den Worker als gewöhnliche .js-Datei bündeln. Die
// rohe .mjs-Datei liefern manche Webserver als application/octet-stream aus,
// und der Browser lehnt sie dann als Modul ab (so auf Prod am 03.10.2026).
import PdfWorker from 'pdfjs-dist/build/pdf.worker.min.mjs?worker'
import { apiClient } from '../api/client'

interface Props {
  postId: number
  /** Aktueller Text aus dem Editor. */
  markdown: string
  onClose: () => void
}

interface Theme {
  id: string
  description?: string
}

const DEBOUNCE_MS = 800
const THEME_KEY = 'content_preview_theme'
const FALLBACK_THEMES: Theme[] = [{ id: 'default' }]

type Status = 'idle' | 'waiting' | 'rendering' | 'error'

/** pdf.js wird erst geladen, wenn eine Vorschau offen ist; ein Worker für alle Läufe. */
let pdfjsPromise: Promise<typeof import('pdfjs-dist')> | null = null
function loadPdfjs(): Promise<typeof import('pdfjs-dist')> {
  if (!pdfjsPromise) {
    pdfjsPromise = import('pdfjs-dist').then((pdfjs) => {
      pdfjs.GlobalWorkerOptions.workerPort = new PdfWorker()
      return pdfjs
    })
  }
  return pdfjsPromise
}

/** Stand des letzten Renderlaufs; `key` sagt, für welchen Text und welches Theme. */
interface Phase {
  key: string
  state: 'rendering' | 'idle' | 'error'
}

function readTheme(): string {
  try { return localStorage.getItem(THEME_KEY) || 'default' } catch { return 'default' }
}

export function PdfPreviewPanel({ postId, markdown, onClose }: Props) {
  const [themes, setThemes] = useState<Theme[]>(FALLBACK_THEMES)
  const [theme, setTheme] = useState<string>(readTheme)
  const [phase, setPhase] = useState<Phase>({ key: '', state: 'idle' })
  const [error, setError] = useState('')
  const [pages, setPages] = useState(0)
  const [seconds, setSeconds] = useState<number | null>(null)
  const [pdfUrl, setPdfUrl] = useState<string | null>(null)
  const pagesRef = useRef<HTMLDivElement | null>(null)
  const runRef = useRef(0)

  useEffect(() => {
    let alive = true
    apiClient
      .listExportThemes(postId)
      .then((list) => {
        if (!alive || list.length === 0) return
        setThemes(list)
        // Ein gemerktes Theme, das es nicht mehr gibt, fällt auf das erste zurück.
        setTheme((t) => (list.some((x) => x.id === t) ? t : list[0].id))
      })
      .catch(() => { /* Liste nicht erreichbar: Vorgabe-Theme bleibt wählbar */ })
    return () => { alive = false }
  }, [postId])

  useEffect(() => {
    const run = ++runRef.current
    const abort = new AbortController()
    const key = `${theme}\n${markdown}`
    const timer = window.setTimeout(async () => {
      setPhase({ key, state: 'rendering' })
      setError('')
      const started = performance.now()
      try {
        const data = await apiClient.previewThemedPdf(postId, { markdown, theme }, abort.signal)
        if (run !== runRef.current) return
        const blobUrl = URL.createObjectURL(new Blob([data], { type: 'application/pdf' }))
        const pdfjs = await loadPdfjs()
        // pdf.js übernimmt den Puffer; für den Blob oben wurde er schon kopiert.
        const doc = await pdfjs.getDocument({ data }).promise
        const host = pagesRef.current
        if (!host || run !== runRef.current) { URL.revokeObjectURL(blobUrl); return }
        const width = Math.max(240, host.clientWidth - 32)
        const ratio = window.devicePixelRatio || 1
        const canvases: HTMLCanvasElement[] = []
        for (let n = 1; n <= doc.numPages; n++) {
          const page = await doc.getPage(n)
          const base = page.getViewport({ scale: 1 })
          const viewport = page.getViewport({ scale: (width / base.width) * ratio })
          const canvas = document.createElement('canvas')
          canvas.width = Math.floor(viewport.width)
          canvas.height = Math.floor(viewport.height)
          canvas.className = 'pdf-preview-page'
          canvas.setAttribute('aria-label', `Seite ${n}`)
          const ctx = canvas.getContext('2d')
          if (!ctx) continue
          await page.render({ canvas, canvasContext: ctx, viewport }).promise
          if (run !== runRef.current) { URL.revokeObjectURL(blobUrl); return }
          canvases.push(canvas)
        }
        // Erst tauschen, wenn alle Seiten fertig sind: kein Flackern, und die
        // Scroll-Position bleibt, wo sie war.
        const scrollTop = host.scrollTop
        host.replaceChildren(...canvases)
        host.scrollTop = scrollTop
        setPages(doc.numPages)
        setSeconds((performance.now() - started) / 1000)
        setPdfUrl((old) => { if (old) URL.revokeObjectURL(old); return blobUrl })
        setPhase({ key, state: 'idle' })
      } catch (e) {
        if (abort.signal.aborted || run !== runRef.current) return
        setError(e instanceof Error ? e.message : String(e))
        setPhase({ key, state: 'error' })
      }
    }, DEBOUNCE_MS)
    return () => {
      window.clearTimeout(timer)
      abort.abort()
    }
  }, [postId, markdown, theme])

  useEffect(() => () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl) }, [pdfUrl])

  const chooseTheme = (next: string) => {
    setTheme(next)
    try { localStorage.setItem(THEME_KEY, next) } catch { /* privat */ }
  }

  // Solange der letzte Lauf nicht zum aktuellen Text gehört, wartet die
  // Vorschau auf die Tipp-Pause.
  const status: Status = phase.key === `${theme}\n${markdown}` ? phase.state : 'waiting'

  const label =
    status === 'rendering' ? 'rendert …'
      : status === 'waiting' ? (pages ? 'geändert' : 'wartet …')
        : status === 'error' ? 'Fehler'
          : `${pages} ${pages === 1 ? 'Seite' : 'Seiten'}${seconds !== null ? ` · ${seconds.toFixed(1)} s` : ''}`

  return (
    <aside className="pdf-preview-panel" aria-label="PDF-Vorschau">
      <div className="pdf-preview-bar">
        <span className="pdf-preview-title"><i className="fas fa-file-pdf" /> Vorschau</span>
        <select
          className="pdf-preview-theme"
          value={theme}
          onChange={(e) => chooseTheme(e.target.value)}
          title="Theme wie im Export"
          aria-label="Theme"
        >
          {themes.map((t) => (
            <option key={t.id} value={t.id} title={t.description}>{t.id}</option>
          ))}
        </select>
        <span className={`pdf-preview-status pdf-preview-status--${status}`}>{label}</span>
        <span className="pdf-preview-spacer" />
        {pdfUrl && (
          <a className="pdf-preview-open" href={pdfUrl} target="_blank" rel="noreferrer" title="PDF in neuem Tab öffnen">
            <i className="fas fa-external-link-alt" />
          </a>
        )}
        <button type="button" className="pdf-preview-close" onClick={onClose} title="Vorschau schließen" aria-label="Vorschau schließen">
          <i className="fas fa-times" />
        </button>
      </div>
      {status === 'error' && <div className="pdf-preview-error">Vorschau nicht möglich: {error}</div>}
      <div ref={pagesRef} className={`pdf-preview-pages${status === 'rendering' || status === 'waiting' ? ' is-stale' : ''}`} />
    </aside>
  )
}
